import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdCode, MdSmartphone, MdPalette, MdBarChart, MdBrush } from 'react-icons/md';
import axiosInstance from '../../api/axiosInstance';

// Icon mapping
const iconMap = {
  MdCode,
  MdSmartphone,
  MdPalette,
  MdSearch,
  MdBarChart,
  MdBrush,
};

const ServiceList = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axiosInstance.get('/service');
        setServices(response.data.data || []);
      } catch (err) {
        console.error('Failed to fetch services:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const filteredServices = services.filter((service) =>
    service.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await axiosInstance.delete(`/service/${id}`);
        const response = await axiosInstance.get('/service');
        setServices(response.data.data || []);
      } catch (err) {
        alert('Failed to delete service');
        console.error('Delete error:', err);
      }
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Services</h1>
        <Link
          to="/admin/services/create"
          className="flex items-center justify-center gap-2 bg-[#008300] text-white px-4 py-2 rounded-lg hover:bg-[#006600] transition-colors"
        >
          <MdAdd />
          <span>Add Service</span>
        </Link>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12 text-gray-500">
          Loading services...
        </div>
      )}

      {/* Service Table */}
      {!loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700">
                Icon
              </th>
              <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700">
                Title
              </th>
              <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700 hidden sm:table-cell">
                Description
              </th>
              <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700 hidden md:table-cell">
                Route (Learn More)
              </th>
              <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredServices.map((service) => (
              <tr key={service.id} className="hover:bg-gray-50">
                <td className="px-4 sm:px-6 py-3 sm:py-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#008300]/10 rounded-lg flex items-center justify-center text-[#008300]">
                    {iconMap[service.icon] ? React.createElement(iconMap[service.icon], { className: "text-base sm:text-lg" }) : <span className="text-base sm:text-lg">{service.icon}</span>}
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-gray-800 text-sm sm:text-base">
                  {service.title}
                </td>
                <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-600 text-sm hidden sm:table-cell">
                  <p className="text-sm text-gray-600 truncate max-w-[150px] sm:max-w-xs">
                    {service.description}
                  </p>
                </td>
                <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-600 text-sm hidden md:table-cell">
                  <span className="text-xs font-mono bg-green-50 text-[#008300] border border-green-200 px-2 py-1 rounded">
                    {service.path || <span className="text-gray-400 italic">not set</span>}
                  </span>
                </td>
                <td className="px-4 sm:px-6 py-3 sm:py-4">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/admin/services/edit/${service.id}`}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <MdEdit />
                    </Link>
                    <button
                      onClick={() => handleDelete(service.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <MdDelete />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {filteredServices.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No services found
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default ServiceList;
